import { z } from 'zod';
import {
  assetSeedSchema,
  assetSortSchema,
  publishStateFilterSchema,
  sortDirSchema,
  EXIF_TRACKED_FIELDS,
  type AssetSort,
  type ExifTrackedField,
  type PublishStateFilter,
  type SortDir,
} from '@xphoto/shared';
import {
  INITIAL_GALLERY_STATE,
  type GalleryState,
  type GalleryTab,
  type MediaTypeFilter,
} from './state';

/**
 * Allow-list des valeurs du filtre Type dans l'URL
 * (`type=image|animatedImage|video|videoWithSound`). « Tous » = clé absente. Toute
 * autre valeur est ignorée (retour à « Tous »).
 */
const mediaTypeSchema = z.enum(['image', 'animatedImage', 'video', 'videoWithSound']);

/**
 * Sérialisation de l'état de la galerie DANS L'URL (fondation « tout dans l'URL »).
 *
 * L'onglet est porté par le CHEMIN (`/`, `/dossiers`, `/tags`) ; le
 * reste de l'état (tri, sens, recherche, dossier/tag sélectionné, filtres EXIF du
 * lien profond) est porté par la QUERY-STRING, avec des clés en français cohérentes
 * avec le futur fil d'Ariane :
 *
 *   `tri`     ← `sort`         `ordre`   ← `dir`
 *   `q`       ← `search`       `dossier` ← `folder`
 *   `tag`     ← `tag`          `champ`   ← `hasExifField`
 *   `publie`  ← `publishState`
 *   `type`    ← `mediaType` (filtre Type image/animatedImage/vidéo/vidéo avec son ;
 *              omis quand « Tous » ; `videoWithSound` porte la contrainte `hasAudio`)
 *
 * Recherche avancée (panneau dédié), clés FR omises aux valeurs par défaut/vides :
 *   `note_min`  ← `ratingMin` (entier 1..5 ; omis à `null`/0)
 *   `tags`      ← `tagIds`    (CSV d'uuid ; omis si vide)
 *   `tags_mode` ← `tagMode`   (`all`/`any` ; omis en `any` par défaut ou sans tag)
 *   `filtre`    ← `expr`      (expression booléenne en texte ; omis si vide)
 *
 * Ces fonctions sont PURES et testées : la sérialisation omet les valeurs par
 * défaut/vides (URL propre), la désérialisation valide via les schémas partagés
 * (jamais de valeur brute injectée dans l'état) et retombe proprement sur les
 * valeurs par défaut. La densité S/M/L n'est PAS dans l'URL (préférence d'UI
 * locale, pas un « emplacement » adressable) — la GRAINE d'aléatoire non plus depuis
 * le LOT C1 (elle est persistée par compte côté serveur ; cf. `sort-resolution.ts`).
 */

/** Chemin de chaque onglet de galerie (segments en français). */
const TAB_PATH: Record<GalleryTab, string> = {
  gallery: '/',
  folders: '/dossiers',
  tags: '/tags',
};

/** Table inverse chemin → onglet. */
const PATH_TAB: Record<string, GalleryTab> = {
  '/': 'gallery',
  '/dossiers': 'folders',
  '/tags': 'tags',
};

/** Chemin (sans query) de l'onglet demandé. */
export function tabToPath(tab: GalleryTab): string {
  return TAB_PATH[tab];
}

/** Onglet correspondant à un `pathname` de galerie (défaut : `gallery`). */
export function pathToTab(pathname: string): GalleryTab {
  const normalized = pathname !== '/' && pathname.endsWith('/') ? pathname.slice(0, -1) : pathname;
  return PATH_TAB[normalized] ?? 'gallery';
}

/**
 * Sérialise l'état de galerie en `URLSearchParams` (hors onglet, qui est le chemin).
 * Les valeurs par défaut et les champs non pertinents pour l'onglet courant sont
 * omis pour garder l'URL propre.
 */
export function galleryStateToSearchParams(state: GalleryState): URLSearchParams {
  const sp = new URLSearchParams();
  // Tri : TOUJOURS sérialisé `tri=<clé>`, y compris `tri=random` et y compris quand il vaut
  // le repli neutre `INITIAL_GALLERY_STATE.sort` (`takenAt`).
  //
  // L'omettre « pour une URL propre » PERDAIT le choix de l'utilisateur : une URL sans
  // marqueur de tri signifie « aucun tri explicite ⇒ applique la PRÉFÉRENCE »
  // (`sort-resolution.ts`, défaut de fabrique `random`). Choisir « Date de prise de vue »
  // produisait donc une URL indiscernable d'une arrivée à froid sur `/`, et la préférence
  // réécrasait aussitôt le choix (tri inopérant, radio qui revient sur « Aléatoire »,
  // régression v1.21.3). Le tri effectif doit rester LISIBLE dans l'URL.
  //
  // La GRAINE, elle, n'est PLUS sérialisée (LOT C1) : `?graine=PCTGPpKoHFJH` salissait la
  // barre d'adresse sans rien apporter, puisque la graine est déjà persistée PAR COMPTE
  // côté serveur (`CurrentUser.randomSeed`) et ré-injectée par `sort-resolution.ts`.
  // `tri=random` suffit à porter « l'ordre est aléatoire ».
  sp.set('tri', state.sort);
  // Le sens n'a pas de sens en aléatoire (ordre pseudo-uniforme) → jamais `ordre` alors.
  if (state.sort !== 'random' && state.dir !== INITIAL_GALLERY_STATE.dir)
    sp.set('ordre', state.dir);
  const q = state.search.trim();
  if (q) sp.set('q', q);
  // Le dossier/tag n'a de sens que sur son onglet (évite de traîner un filtre inerte).
  if (state.tab === 'folders' && state.folder) sp.set('dossier', state.folder);
  if (state.tab === 'tags' && state.tag) sp.set('tag', state.tag);
  // Filtres EXIF (lien profond) — axes indépendants de l'onglet.
  if (state.hasExifField) sp.set('champ', state.hasExifField);
  if (state.publishState) sp.set('publie', state.publishState);
  // Filtre Type (image/animatedImage/vidéo/vidéo avec son) — omis quand « Tous » (absent).
  if (state.mediaType) sp.set('type', state.mediaType);
  // Filtre « taguées » (système « Taguer ») — `taguees=non` (non taguées) / `taguees=oui`
  // (taguées) / `taguees=avalider` (proposées par l'IA, pas encore validées) ; omis
  // quand pas de filtre. Les trois valeurs sont mutuellement exclusives.
  if (state.pendingAiTags) sp.set('taguees', 'avalider');
  else if (state.tagged === false) sp.set('taguees', 'non');
  else if (state.tagged === true) sp.set('taguees', 'oui');
  // Recherche avancée — axes indépendants ; omis aux valeurs vides/par défaut (URL propre).
  if (state.ratingMin !== null && state.ratingMin > 0) sp.set('note_min', String(state.ratingMin));
  if (state.tagIds.length > 0) {
    sp.set('tags', state.tagIds.join(','));
    // `tags_mode` n'a de sens qu'avec des tags ; omis en `any` (défaut).
    if (state.tagMode === 'all') sp.set('tags_mode', 'all');
  }
  // Filtre AVANCÉ (expression booléenne) → `?filtre=<texte>` (URL-encodé par
  // `URLSearchParams`). Omis quand vide/absent (URL propre). La syntaxe n'est PAS validée
  // ici (module pur) : une expression invalide est passée telle quelle et l'UI/API la rejette.
  if (state.expr !== undefined && state.expr.trim() !== '') sp.set('filtre', state.expr);
  return sp;
}

/** Query-string (sans « ? ») équivalente à un état de galerie. */
export function gallerySearchString(state: GalleryState): string {
  return galleryStateToSearchParams(state).toString();
}

/** Chemin + query complets pour un état de galerie (lien partageable). */
export function galleryLinkFor(state: GalleryState): string {
  const search = gallerySearchString(state);
  const path = tabToPath(state.tab);
  return search ? `${path}?${search}` : path;
}

/**
 * Lien vers l'onglet « Dossiers » OUVERT sur un dossier donné (`?dossier=<chemin>`).
 * `path` est un chemin relatif POSIX (`''` = racine).
 *
 * SOURCE UNIQUE de ce format d'URL : le fil d'Ariane (`Breadcrumb`), la légende de la
 * visionneuse (`Viewer`) et le panneau « Détail » le construisaient chacun à la main.
 * Volontairement PAS bâti sur `galleryLinkFor` : celui-ci sérialise via
 * `URLSearchParams` (espace → `+`) et OMET `dossier` à la racine, alors qu'on veut ici
 * l'encodage `encodeURIComponent` (espace → `%20`) et la clé toujours présente.
 */
export function folderLinkFor(path: string): string {
  return `${tabToPath('folders')}?dossier=${encodeURIComponent(path)}`;
}

/** Membre valide d'un enum Zod (`safeParse`), ou `undefined`. */
function parseEnum<T>(
  schema: { safeParse: (v: unknown) => { success: boolean; data?: unknown } },
  raw: string | null,
): T | undefined {
  if (raw === null) return undefined;
  const parsed = schema.safeParse(raw);
  return parsed.success ? (parsed.data as T) : undefined;
}

/** Champ EXIF suivi valide (allow-list partagée), ou `undefined`. */
function parseExifField(raw: string | null): ExifTrackedField | undefined {
  return raw !== null && (EXIF_TRACKED_FIELDS as readonly string[]).includes(raw)
    ? (raw as ExifTrackedField)
    : undefined;
}

/** Graine d'aléatoire valide (token borné `ASSET_SEED_PATTERN`), ou `undefined`. */
function parseSeed(raw: string | null): string | undefined {
  if (raw === null) return undefined;
  const parsed = assetSeedSchema.safeParse(raw);
  return parsed.success ? parsed.data : undefined;
}

/** Uuid valide (validation des ids de tags de la recherche avancée). */
const uuidSchema = z.uuid();

/**
 * Note minimale valide (entier 1..5). `null` si absent/invalide/0 (0 = « toutes » ⇒
 * pas de filtre — cohérent avec l'omission côté sérialisation).
 */
function parseRatingMin(raw: string | null): number | null {
  if (raw === null) return null;
  const n = Number(raw);
  return Number.isInteger(n) && n >= 1 && n <= 5 ? n : null;
}

/** Ids de tags cochés (CSV d'uuid, dédupliqués ; entrées non-uuid ignorées). */
function parseTagIds(raw: string | null): string[] {
  if (raw === null) return [];
  const seen = new Set<string>();
  for (const part of raw.split(',')) {
    const value = part.trim();
    if (uuidSchema.safeParse(value).success) seen.add(value);
  }
  return [...seen];
}

/**
 * Reconstruit un `GalleryState` complet depuis le chemin (onglet) et la query.
 * Valide chaque champ via les schémas partagés — une valeur inconnue est ignorée
 * (retour au défaut), jamais propagée telle quelle.
 */
export function searchParamsToGalleryState(
  pathname: string,
  search: URLSearchParams,
): GalleryState {
  const tab = pathToTab(pathname);
  // Ordre de tri, dérivé de l'URL SEULE (module PUR : aucun accès storage/préférence) :
  //  - `tri=<clé>` valide      → ce tri explicite (`tri=random` compris) ;
  //  - sinon `graine=<seed>`   → ALÉATOIRE — forme LEGACY d'avant le LOT C1, où l'aléatoire
  //    se sérialisait par sa seule graine. Toujours acceptée pour que les FAVORIS et les
  //    liens DÉJÀ PARTAGÉS rendent le même ordre ; l'URL se nettoie d'elle-même en
  //    `?tri=random` à la première navigation qui re-sérialise l'état ;
  //  - sinon                   → repli NEUTRE `INITIAL_GALLERY_STATE.sort` (déterministe).
  // Le VRAI défaut de l'Accueil (préférence utilisateur, souvent aléatoire) et la graine du
  // COMPTE sont appliqués par-dessus, hors de ce module pur, par `sort-resolution.ts`.
  const urlSeed = parseSeed(search.get('graine'));
  const triSort = parseEnum<AssetSort>(assetSortSchema, search.get('tri'));
  const sort: AssetSort =
    triSort ?? (urlSeed !== undefined ? 'random' : INITIAL_GALLERY_STATE.sort);
  const state: GalleryState = {
    ...INITIAL_GALLERY_STATE,
    tab,
    sort,
    dir: parseEnum<SortDir>(sortDirSchema, search.get('ordre')) ?? INITIAL_GALLERY_STATE.dir,
    search: search.get('q') ?? '',
  };

  // Graine reprise d'une URL LEGACY (`?graine=`) en aléatoire — rétro-compatibilité des
  // favoris/liens partagés. Les URL fraîches n'en portent plus : `sort-resolution.ts`
  // injecte alors la graine du COMPTE.
  if (sort === 'random' && urlSeed !== undefined) state.seed = urlSeed;

  if (tab === 'folders') {
    const folder = search.get('dossier');
    if (folder) state.folder = folder;
  }
  if (tab === 'tags') {
    const tag = search.get('tag');
    if (tag) state.tag = tag;
  }

  const field = parseExifField(search.get('champ'));
  if (field) state.hasExifField = field;

  const publish = parseEnum<PublishStateFilter>(publishStateFilterSchema, search.get('publie'));
  if (publish) state.publishState = publish;

  // Filtre Type : valeur validée (`image`/`animatedImage`/`video`/`videoWithSound`)
  // ou absent → « Tous ».
  const mediaType = parseEnum<MediaTypeFilter>(mediaTypeSchema, search.get('type'));
  if (mediaType) state.mediaType = mediaType;

  // Filtre « taguées » : `taguees=non` → non taguées, `taguees=oui` → taguées,
  // `taguees=avalider` → proposées par l'IA à valider (`pendingAiTags`), sinon absent.
  const taguees = search.get('taguees');
  if (taguees === 'avalider') state.pendingAiTags = true;
  else if (taguees === 'non') state.tagged = false;
  else if (taguees === 'oui') state.tagged = true;

  // Recherche avancée (les défauts vides viennent d'`INITIAL_GALLERY_STATE` ci-dessus ;
  // on n'écrase que si l'URL porte une valeur valide → état frais = filtres vides).
  const ratingMin = parseRatingMin(search.get('note_min'));
  if (ratingMin !== null) state.ratingMin = ratingMin;

  const tagIds = parseTagIds(search.get('tags'));
  if (tagIds.length > 0) {
    state.tagIds = tagIds;
    // `tags_mode=all` explicite ; toute autre valeur (ou absent) ⇒ `any` (défaut).
    if (search.get('tags_mode') === 'all') state.tagMode = 'all';
  }

  // Filtre AVANCÉ : `?filtre=<texte>` → `state.expr` (omis si vide/absent). Validé plus
  // tard (UI/API) ; ici on ne fait que le transporter.
  const expr = search.get('filtre');
  if (expr !== null && expr.trim() !== '') state.expr = expr;

  return state;
}
